import { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';

import { InsurancePolicies_InsurancePolicyFragment } from '@/modules/entities/InsurancePolicyDetailsSubform/graphql/InsurancePolicies.generated';
import { InsurancePolicyTypeCopyMap } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

export interface ManuallyTrackedPoliciesTableRowData {
  id: string;
  policyType: string;
  insured: string;
  cashValue: string;
  deathBenefitAmount: string;
}

export function mapPoliciesToRows(
  policies: InsurancePolicies_InsurancePolicyFragment[]
): ManuallyTrackedPoliciesTableRowData[] {
  return policies.map((policy) => ({
    id: policy.id,
    policyType: InsurancePolicyTypeCopyMap[policy.kind] || 'Unknown',
    insured:
      policy.policyHolders?.map((holder) => holder?.displayName).join(', ') ||
      '-',
    cashValue: policy.cashValue
      ? formatCurrencyNoDecimals(policy.cashValue)
      : '-',
    deathBenefitAmount: policy.deathBenefitAmount
      ? formatCurrencyNoDecimals(policy.deathBenefitAmount)
      : '-',
  }));
}

/**
 * Extracts insurance policies from an entity if it has them
 */
export function extractPoliciesFromEntity(
  entity: EstatePage_EntityFragment
): InsurancePolicies_InsurancePolicyFragment[] {
  const subtype = entity.subtype;

  // Check if the subtype has policies
  if ('policies' in subtype && subtype.policies) {
    return subtype.policies;
  }

  return [];
}

export function useColumns(): GridColDef<ManuallyTrackedPoliciesTableRowData>[] {
  return useMemo(
    () => [
      {
        field: 'policyType',
        headerName: 'Policy type',
        width: 175,
        flex: 1,
      },
      {
        field: 'insured',
        headerName: 'Insured',
        width: 175,
        flex: 1,
      },
      {
        field: 'cashValue',
        headerName: 'Cash value',
        width: 175,
        flex: 1,
      },
      {
        field: 'deathBenefitAmount',
        headerName: 'Death benefit amount',
        width: 175,
        flex: 1,
      },
    ],
    []
  );
}
